import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import instance from '../api/axios';
import ConfirmModal from './ConfirmModal';

const ActiveRound = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scores, setScores] = useState({});
    const [errors, setErrors] = useState({});
    const [viewingRound, setViewingRound] = useState(1);
    const [unlockConfirm, setUnlockConfirm] = useState({ isOpen: false, groupIndex: null, groupPlayers: [] });
    const [unlockLoading, setUnlockLoading] = useState(false);

    // --- PHASE 5: STRICT ROLE CHECKS ---
    const userString = localStorage.getItem('user');
    const user = userString ? JSON.parse(userString) : null;
    
    const userId = (user?.id || user?._id)?.toString();
    const userRole = user?.role || 'player';
    
    const isAdmin = userRole === 'admin';
    
    // Check: Are you an admin, OR tournament creator/master who owns this specific tournament
    const masterIdStr = tournament?.masterId ? (tournament.masterId._id || tournament.masterId).toString() : '';
    const isMaster = isAdmin || (userRole === 'master' && masterIdStr && userId && masterIdStr === userId);
    const canManageScores = isAdmin || isMaster;
    
    useEffect(() => {
        fetchTournament();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (tournament && tournament.status === 'completed' && !isAdmin) {
            navigate(`/tournament/${id}/standings`); 
        }
        if (tournament && !loading) {
            setViewingRound(tournament.currentRound);
        }
    }, [tournament, navigate, id, isAdmin, loading]);

    useEffect(() => {
        setScores({});
        setErrors({});
    }, [viewingRound]);

    useEffect(() => {
        if (tournament) {
            setScores(prevScores => {
                const updatedScores = { ...prevScores };
                const displayRoundData = tournament.rounds?.find(r => r.roundNumber === viewingRound);
                if (displayRoundData) {
                    displayRoundData.groups.forEach((group, gIndex) => {
                        if (!updatedScores[gIndex]) updatedScores[gIndex] = {};
                        group.players.forEach(p => {
                            if (p.gamePts !== null && p.gamePts !== undefined) {
                                if (updatedScores[gIndex][p.playerId] === undefined || updatedScores[gIndex][p.playerId] === '') {
                                    updatedScores[gIndex][p.playerId] = p.gamePts;
                                }
                            }
                        });
                    });
                }
                return updatedScores;
            });
        }
    }, [tournament, viewingRound]);

    const fetchTournament = async () => {
        try {
            const { data } = await instance.get(`/tournaments/${id}`);
            setTournament(data);
            setLoading(false);
        } catch (err) {
            console.error("Failed to load tournament", err);
            setLoading(false);
        }
    };

    // --- PHASE 4 UNIVERSAL ENGINE VARIABLES ---
    const gameType = tournament?.settings?.gameType || 'Catan';
    const format = tournament?.settings?.format || 'Swiss';
    const currentStage = tournament?.stage || tournament?.settings?.stage || 'preliminary';
    const isCatanSeafarers = gameType === 'Catan Seafarers';
    const isCatan = gameType === 'Catan' || isCatanSeafarers || format === 'Official Catan';
    const winThreshold = isCatanSeafarers ? 14 : 10;
    const gameNameDisplay = isCatanSeafarers ? 'Catan Seafarers' : 'Catan';
    // ------------------------------------------

    const handleScoreChange = (groupId, playerId, value) => {
        const numValue = value === '' ? '' : Number(value);
        setScores(prev => {
            const updatedGroup = { ...(prev[groupId] || {}), [playerId]: numValue };
            const newScores = { ...prev, [groupId]: updatedGroup };

            // Real-time check: only one player can have winThreshold (10 or 14) points or higher
            if (isCatan && numValue !== '' && numValue >= winThreshold) {
                const otherWinCount = Object.entries(updatedGroup).filter(
                    ([pId, score]) => pId !== playerId && score !== '' && score !== null && Number(score) >= winThreshold
                );
                if (otherWinCount.length > 0) {
                    setErrors(prevErr => ({
                        ...prevErr,
                        [groupId]: `In ${gameNameDisplay} tournaments, only one player can have ${winThreshold} points or higher.`
                    }));
                    return newScores;
                }
            }

            setErrors(prevErr => ({ ...prevErr, [groupId]: null }));
            return newScores;
        });
    };

    // --- PHASE 4 DYNAMIC VALIDATION ---
    const validateGroup = (groupId, groupPlayers) => {
        if (groupPlayers.length === 1) return null; 
        const groupInput = scores[groupId] || {};
        const playerScores = groupPlayers.map(p => {
            const val = groupInput[p.playerId];
            if (val !== undefined && val !== '') return val;
            if (p.gamePts !== null && p.gamePts !== undefined) return p.gamePts;
            return val;
        });

        // Universal Rule: All scores must be entered
        if (playerScores.length !== groupPlayers.length || playerScores.includes(undefined) || playerScores.includes('')) {
            return "All player scores must be entered.";
        }

        // Catan / Catan Seafarers Specific Rules
        if (isCatan) {
            const numScores = playerScores.map(Number);
            if (numScores.some(isNaN)) {
                return "All player scores must be valid numbers.";
            }
            if (Math.min(...numScores) < 2) {
                return "The minimum Game Points allowed is 2.";
            }
            const winCount = numScores.filter(s => s >= winThreshold).length;
            if (winCount > 1) {
                return `In ${gameNameDisplay} tournaments, only one player can have ${winThreshold} points or higher.`;
            }
            if (winCount === 0) {
                return `There must be exactly one player with ${winThreshold} or more Game Points (the winner).`;
            }
        } 
        
        // Knockout Specific Rules
        else if (format === 'Knockout') {
            // Check for ties in 1v1 formats
            if (playerScores.length === 2 && playerScores[0] === playerScores[1]) {
                return "Knockout matches cannot end in a tie! Please enter a decisive score.";
            }
        }

        return null; 
    };

    const submitGroup = async (groupId, groupPlayers) => {
        const validationError = validateGroup(groupId, groupPlayers);
        if (validationError) {
            setErrors(prev => ({ ...prev, [groupId]: validationError }));
            return;
        }

        const isBye = groupPlayers.length === 1;
        const groupInput = scores[groupId] || {};

        const payload = groupPlayers.map(p => {
            let val = groupInput[p.playerId];
            if (val === undefined || val === '') {
                val = p.gamePts;
            }
            return {
                playerId: p.playerId,
                // If it's a BYE, automatically send a '1' so they win the match
                gamePts: isBye ? 1 : val
            };
        });

        try {
            await instance.post(`/tournaments/${id}/submit-group`, { 
                groupScores: payload,
                roundNumber: viewingRound 
            });
            fetchTournament(); 
        } catch (err) {
            setErrors(prev => ({ ...prev, [groupId]: err.response?.data?.error || "Submission failed" }));
        }
    };

    const promptUnlockGroup = (groupIndex, groupPlayers) => {
        setUnlockConfirm({
            isOpen: true,
            groupIndex,
            groupPlayers
        });
    };

    const handleConfirmUnlock = async () => {
        const { groupIndex, groupPlayers } = unlockConfirm;
        if (groupIndex === null || !groupPlayers) return;

        setUnlockLoading(true);
        try {
            const firstPlayer = groupPlayers[0];
            const pId = firstPlayer ? (firstPlayer.playerId || firstPlayer.id) : null;
            await instance.post(`/tournaments/${id}/unlock-group`, { 
                playerId: pId,
                groupIndex: groupIndex,
                roundNumber: viewingRound
            });
            // Keep the previous scores populated in input fields
            setErrors(prev => ({ ...prev, [groupIndex]: null }));
            await fetchTournament(); 
        } catch (err) {
            console.error("Failed to unlock group:", err);
            const msg = err.response?.data?.message || err.response?.data?.error || "Failed to unlock group.";
            setErrors(prev => ({ ...prev, [groupIndex]: msg }));
        } finally {
            setUnlockLoading(false);
            setUnlockConfirm({ isOpen: false, groupIndex: null, groupPlayers: [] });
        }
    };

    if (loading) return (
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );
    
    if (!tournament) return <div className="text-center py-20 text-gray-500 font-bold">Tournament not found.</div>;

    const displayRoundData = tournament.rounds.find(r => r.roundNumber === viewingRound);

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* STICKY HEADER AREA */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="text-center md:text-left">
                        <h1 className="text-2xl font-black text-gray-900 tracking-tight">{tournament.name}</h1>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2 justify-center md:justify-start">
                            <span>{gameType} • {format}</span>
                            {format === 'Official Catan' && (
                                <span className="bg-blue-100 text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full capitalize">
                                    {currentStage} Stage
                                </span>
                            )}
                        </p>
                    </div>

                    {/* ROUND SELECTOR */}
                    <div className="flex bg-gray-100 p-1 rounded-xl shadow-inner overflow-x-auto max-w-full">
                        {tournament.rounds.map(r => (
                            <button 
                                key={r.roundNumber}
                                onClick={() => setViewingRound(r.roundNumber)}
                                className={`px-5 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all duration-200 ${
                                    viewingRound === r.roundNumber 
                                    ? 'bg-white text-blue-600 shadow-sm scale-105' 
                                    : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                                }`}
                            >
                                Round {r.roundNumber}
                            </button>
                        ))}
                    </div>

                    <button 
                        onClick={() => navigate(`/tournament/${id}/standings`)}
                        className="bg-gray-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-blue-600 transition-colors shadow-lg"
                    >
                        Live Standings
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8">
                <div className="flex items-center gap-3 mb-8">
                    <h2 className="text-3xl font-black text-gray-800">Round {viewingRound}</h2>
                    <div className="h-px flex-1 bg-gray-200"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {displayRoundData?.groups.map((group, groupIndex) => {
                        const isGroupSubmitted = group.isSubmitted !== undefined 
                            ? Boolean(group.isSubmitted) 
                            : group.players.every(p => p.gamePts !== null && p.gamePts !== undefined);

                        return (
                            <div key={groupIndex} className={`bg-white rounded-3xl border-2 transition-all duration-300 overflow-hidden flex flex-col ${
                                isGroupSubmitted ? 'border-transparent shadow-sm grayscale-[0.5] opacity-90' : 'border-blue-100 shadow-xl shadow-blue-50'
                            }`}>
                                {/* Header */}
                                <div className={`px-6 py-4 flex justify-between items-center ${isGroupSubmitted ? 'bg-gray-50 text-gray-500' : 'bg-blue-600 text-white'}`}>
                                    <span className="font-black tracking-tighter text-lg italic">
                                        {format === 'Knockout' ? `MATCH ${groupIndex + 1}` : `GROUP ${groupIndex + 1}`}
                                    </span>
                                    {isGroupSubmitted ? (
                                        <div className="bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-[10px] font-bold uppercase">Locked</div>
                                    ) : (
                                        <div className="bg-blue-400 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase animate-pulse">Live</div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="p-6 space-y-4 flex-grow">
                                    {errors[groupIndex] && (
                                        <div className="bg-red-50 text-red-600 p-3 rounded-xl text-xs font-bold border border-red-100 animate-bounce">
                                            ⚠️ {errors[groupIndex]}
                                        </div>
                                    )}

                                    <div className="space-y-3">
                                        {group.players.map(player => {
                                            const playerDetails = tournament.players.find(p => p.id === player.playerId);
                                            return (
                                                <div key={player.playerId} className="flex items-center justify-between group">
                                                    <span className="font-bold text-gray-700 group-hover:text-blue-600 transition-colors">
                                                        {playerDetails?.name || 'Unknown'}
                                                    </span>
                                                    
                                                    {/* PHASE 4 FIX: BYE AUTO-ADVANCE UI */}
                                                    {group.players.length === 1 ? (
                                                        <span className="text-[10px] font-black text-gray-400 bg-gray-100 px-3 py-2 rounded-xl border border-gray-200 uppercase tracking-widest">
                                                            Auto-Advance
                                                        </span>
                                                    ) : (
                                                        <input type="number" 
                                                            min={isCatan ? "2" : undefined} 
                                                            value={scores[groupIndex]?.[player.playerId] !== undefined ? scores[groupIndex][player.playerId] : (player.gamePts ?? '')}
                                                            onChange={(e) => handleScoreChange(groupIndex, player.playerId, e.target.value)}
                                                            disabled={isGroupSubmitted || !canManageScores}
                                                            placeholder={isCatan ? "-" : "Score"}
                                                            className={`w-16 h-12 text-center text-lg font-black rounded-xl border-2 transition-all outline-none ${
                                                                isGroupSubmitted || !canManageScores
                                                                ? 'bg-gray-100 border-gray-100 text-gray-400 cursor-not-allowed' 
                                                                : 'bg-gray-50 border-gray-100 focus:border-blue-500 focus:bg-white text-gray-900 shadow-inner'
                                                            }`}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="p-4 bg-gray-50 border-t border-gray-100">
                                    {!isGroupSubmitted ? (
                                        canManageScores ? (
                                            <button 
                                                onClick={() => submitGroup(groupIndex, group.players)}
                                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-3 rounded-2xl shadow-lg shadow-blue-100 transition-transform active:scale-95"
                                            >
                                                 {group.players.length === 1 ? 'CONFIRM BYE ⏩' : 'SUBMIT SCORES'}
                                            </button>
                                        ) : (
                                            <div className="w-full text-center py-2.5 text-xs font-bold text-gray-400 bg-gray-100 rounded-xl border border-gray-200">
                                                🔒 Scores submitted by Tournament Master
                                            </div>
                                        )
                                    ) : (
                                        canManageScores && (
                                            <button 
                                                type="button"
                                                onClick={() => promptUnlockGroup(groupIndex, group.players)}
                                                className="w-full bg-white border-2 border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-400 font-bold py-2.5 rounded-2xl transition-all flex items-center justify-center gap-2 shadow-sm"
                                            >
                                                <span>Unlock Scores</span>
                                                <span className="text-xl">🔓</span>
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* UNLOCK CONFIRMATION MODAL */}
            <ConfirmModal
                isOpen={unlockConfirm.isOpen}
                onClose={() => setUnlockConfirm({ isOpen: false, groupIndex: null, groupPlayers: [] })}
                onConfirm={handleConfirmUnlock}
                title="Unlock Table Scores"
                message={`Are you sure you want to unlock Table ${(unlockConfirm.groupIndex ?? 0) + 1}? Previously submitted scores will remain in place so you can edit and resubmit them.`}
                confirmText={unlockLoading ? "Unlocking..." : "Unlock Table"}
                confirmColor="bg-orange-500 hover:bg-orange-600"
            />
        </div>
    );
};

export default ActiveRound;